// --------------------------------------------------------
// OMS Questionnaire - OMSQuestionnaire
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import React, { useCallback, useEffect, useState } from "react";
import {
  GeneralContainer,
  GeneralContainerQuestionnaire,
  H2,
  H3,
  Text,
} from "../../components/UI/generalLayout/Layout.styled";
import ScreenHeader from "../../components/UI/screenHeader/ScreenHeader";
import useResponsiveInfo from "../../hooks/useResponsiveInfo";
import OMSQuestion from "./OMSQuestion";
import { ReactComponent as RightArrow } from "../../assets/imgs/rightArrow.svg";
import { GenericSolidButton } from "../../components/UI/buttons/Button.styled";
import Colors from "../../constants/Colors";
import { useSelector } from "react-redux";
import * as questionnaireActions from "../../store/actions/questionnaire";
import * as loginActions from "../../store/actions/login";
import { useAppDispatch } from "../..";
import {
  OMSQuestionnaireTitle,
  OMSQuestionnaireFirstParagraph,
  OMSQuestionnaireSecondParagraph,
  OMSQuestionnaireThirdParagraph,
  OMSQuestionnaireReference1,
  OMSQuestionnaireReference2,
} from "../../constants/Texts";
import { Paths } from "../../enums/Paths";
import { useLocation, useNavigate } from "react-router-dom";
import { handleBackendErrors } from "../../helpers/HelperFunctions";
import { CircularProgress } from "@mui/material";
import { PersonService } from "../../core/http/service/PersonService";
import BaseResponse from "../../models/core/BaseResponse";
import AvaliacaoResposta from "../../models/core/questionnaire/AvaliacaoResposta";
import * as personActions from "../../store/actions/person";
import HeaderLogount from "../../components/headerLogount/HeaderLogount";
import ContainerWithSidebar from "../../components/UI/containerWithSidebar/ContainerWithSidebar";
import QuestionnaireHeader from "../../components/UI/header/QuestionnaireHeader";
// --------------------------------------------------------
// OMS Questionnaire INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function OMSQuestionnaire(): JSX.Element {
  // --------------------------------------------------------
  // OMS Questionnaire PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const { isMobile, isTablet, isDesktop } = useResponsiveInfo();
  const [allAnswers, setAllAnswers] = useState([]);
  const [textAnswers, setTextAnswers] = useState<any[]>([]);
  const [textQuestions, setTextQuestions] = useState<any[]>([]);
  const [avaliacao, setAvaliacao] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [questionQuantity, setQuestionQuantity] = useState(0);
  const [textQuestionQuantity, setTextQuestionQuantity] = useState(0);
  const [isFinalizeButtonBlocked, setFinalizeButtonBlocked] = useState(true);
  const { state }: any = useLocation();
  const { isView, pessoaResposta, avaliacaoId } = !!state ? state : { isView: false, pessoaResposta: null, avaliacaoId: null };

  const [demoQuestion] = useState([
    {
      numero: "1",
      pergunta: "Você percebe dos outros o apoio de que necessita?",
    },
  ]);
  const [questionList, setQuestionList] = useState<any[]>([]);
  const [generalErrors, setGeneralErrors] = useState<any[]>([]);
  const [respostas, setRespostas] = useState<any[]>([]);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { questionarioOMS } = useSelector((state: any) => state.questionnaire);
  const { pessoa } = useSelector((state: any) => state.person);
  const { userInfo } = useSelector((state: any) => state.login);

  // --------------------------------------------------------
  const fetchQuestionario = useCallback(async () => {
    await dispatch(questionnaireActions.fetchQuestionarioOMS());
    await dispatch(personActions.fetchPerson(userInfo.id));
  }, [dispatch, userInfo]);

  useEffect(() => {
    fetchQuestionario();
  }, []);

  const fetchAvaliacao = useCallback(
    async (avaliacaoId: string, pessoaId: string) => {
      const service: PersonService = new PersonService();

      const response: any = await service.getAvaliacaoById(
        avaliacaoId,
        pessoaId
      );
      const baseResponse: BaseResponse<any> = new BaseResponse().fromJSON(
        response.data
      );

      const avaliacao = new AvaliacaoResposta().fromJSON({
        ...baseResponse.data,
      });

      return avaliacao;
    },
    []
  );

  const fetchQuestionnaireData = useCallback(async () => {
    if (!isView && questionarioOMS !== null) {
      const questionarioList = userInfo.questionarios.filter((q: any) => {
        return q.questionarioId === questionarioOMS.id;
      });
      const avaliacaoRespondida = questionarioList[0].avaliacaoRespondida;
      if (avaliacaoRespondida !== null) {
        const avaliacao = await fetchAvaliacao(
          avaliacaoRespondida.avaliacaoId,
          userInfo.id
        );
        if (avaliacao.finalizada) {
          // setIsLoading(false)
          navigate(Paths.HMVQuestionnaire);
        }
      } else {
        setAvaliacao(null);
        // setIsLoading(false)
      }
    } else {
      const avaliacao: any = await fetchAvaliacao(avaliacaoId, pessoaResposta.idPessoa);
      setRespostas(avaliacao.respostas)
    }
  }, [questionarioOMS, userInfo, avaliacaoId, pessoaResposta, isView]);

  useEffect(() => {
    fetchQuestionnaireData();
  }, [questionarioOMS]);

  const getRespostasFromPerguntas = (perguntas: any)=>{
    const r = respostas.map(r=> perguntas.filter((p: any)=> p.id === r.perguntaId).length > 0 ? r : '')
    return r.filter(elem=> elem !== '')
  }

  useEffect(() => {
    if (
      allAnswers.length === questionQuantity &&
      textAnswers.length === textQuestionQuantity
    ) {
      setFinalizeButtonBlocked(false);
    } else {
      setFinalizeButtonBlocked(true);
    }
  }, [questionQuantity, textAnswers, allAnswers, textQuestionQuantity]);

  useEffect(() => {
    if (questionarioOMS != null) {
      const questionList: any[] = [];
      const textQuestions: any[] = [];
      let qtdePerguntas = 0;
      let qtdePerguntasTexto = 0;

      questionarioOMS.enunciados.forEach((enunciado: any) => {
        const scalesLabel: any[] = [];
        const perguntas: any[] = [];

        enunciado.alternativas.forEach((alternativa: any) => {
          scalesLabel.push({
            id: alternativa.id,
            texto: alternativa.texto.toUpperCase(),
            codigo: alternativa.codigo,
          });
        });

        if (enunciado.pergunta !== null) {
          qtdePerguntas += enunciado.pergunta.perguntas.length;
          if (enunciado.pergunta.perguntas.length > 0) {
            enunciado.pergunta.perguntas.forEach((pergunta: any) => {
              perguntas.push({
                id: pergunta.id,
                numero: pergunta.ordem,
                pergunta: pergunta.texto,
              });
            });
          } else {
            qtdePerguntasTexto += 1;
            perguntas.push({
              id: enunciado.pergunta.id,
              numero: enunciado.pergunta.ordem,
              pergunta: enunciado.pergunta.texto,
            });
          }
        }

        const question = {
          enunciado:
            enunciado.pergunta !== null ? enunciado.pergunta.texto : "",
          labelEscalas: scalesLabel,
          perguntas: perguntas,
        };
        questionList.push(question);
      });

      setQuestionList(questionList);
      setTextQuestions(textQuestions);
      setQuestionQuantity(qtdePerguntas);
      setTextQuestionQuantity(qtdePerguntasTexto);
    }
  }, [questionarioOMS]);

  const answerQuestionnaire = async () => {
    try {
      const avaliacaoId = avaliacao === null ? null : avaliacao.id;
      await dispatch(
        questionnaireActions.registerQuestionarioOMS(
          questionList,
          allAnswers,
          textAnswers,
          questionarioOMS.id ?? "",
          questionarioOMS.versao ?? 0,
          avaliacaoId,
          pessoa.id
        )
      );
      await dispatch(loginActions.updateUserInfo());
      navigate(Paths.HMVQuestionnaire);
    } catch (error: any) {
      handleBackendErrors(error, setGeneralErrors);
    }
  };

  return (
    <>
      {!isView && (<HeaderLogount />)}

      {isView && (<ContainerWithSidebar>
        <div style={{ marginLeft: 10, marginRight: 10 }}>
          <QuestionnaireHeader pessoa={pessoaResposta} />
          {questionList.map((q) => {
            return (
              <>
                <div style={{ marginBottom: 20 }}>
                  {q.enunciado !== "" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-start",
                      }}
                    >
                      <div style={{ marginTop: 12, marginRight: 15 }}>
                        <RightArrow />
                      </div>
                      <div>
                        <H2>{q.enunciado}</H2>
                      </div>
                    </div>
                  )}

                  <OMSQuestion
                    isView={isView}
                    setTextAnswers={setTextAnswers}
                    setAllAnswers={setAllAnswers}
                    questionario={questionarioOMS}
                    perguntas={q.perguntas}
                    respostas={getRespostasFromPerguntas(q.perguntas)}
                    scalesLabel={q.labelEscalas}
                  />
                </div>
              </>
            );
          })}
        </div>
      </ContainerWithSidebar>)}
      {!isView && (<GeneralContainerQuestionnaire>
        <>
          {isLoading ? (
            <div style={{ textAlign: "center", marginTop: 80 }}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {questionarioOMS != null && (
                <div
                  style={{
                    marginLeft: !isDesktop ? 0 : "12%",
                    marginRight: !isDesktop ? 0 : "12%",
                  }}
                >
                  <ScreenHeader
                    screenTitle={"Questionário de saúde - OMS"}
                    rightButtonText={""}
                    hasRightButton={false}
                    onRightButtonClick={() => { }}
                    errors={generalErrors}
                    onCloseError={() => {
                      setGeneralErrors([]);
                    }}
                  />
                  <H2>{OMSQuestionnaireTitle}</H2>
                  <div style={{ textAlign: "justify" }}>
                    <H3>
                      {OMSQuestionnaireFirstParagraph} <br />
                      <br />
                      {OMSQuestionnaireSecondParagraph}
                    </H3>
                  </div>
                  <div
                    style={{
                      padding: 20,
                      backgroundColor: Colors.GRAY50,
                      borderRadius: 8,
                    }}
                  >
                    <OMSQuestion
                      isView={false}
                      setTextAnswers={setTextAnswers}
                      setAllAnswers={setAllAnswers}
                      questionario={questionarioOMS}
                      perguntas={demoQuestion}
                      isDemo
                      isNoneSelected={true}
                      scalesLabel={[
                        { codigo: "1", texto: "NADA" },
                        { codigo: "2", texto: "MUITO POUCO" },
                        { codigo: "3", texto: "MÉDIO" },
                        { codigo: "4", texto: "MUITO" },
                        { codigo: "5", texto: "COMPLETAMENTE" },
                      ]}
                    />
                    <Text style={{ fontSize: 14 }}>
                      {OMSQuestionnaireThirdParagraph}
                    </Text>
                    <OMSQuestion 
                      isView={false}
                      setTextAnswers={setTextAnswers}
                      setAllAnswers={setAllAnswers}
                      questionario={questionarioOMS}
                      perguntas={demoQuestion}
                      isDemo
                      isNoneSelected={false}
                      scalesLabel={[
                        { codigo: "1", texto: "NADA" },
                        { codigo: "2", texto: "MUITO POUCO" },
                        { codigo: "3", texto: "MÉDIO" },
                        { codigo: "4", texto: "MUITO" },
                        { codigo: "5", texto: "COMPLETAMENTE" },
                      ]}
                    />
                  </div>

                  {questionList.map((q) => {
                    return (
                      <>
                        <div style={{ marginBottom: 20 }}>
                          {q.enunciado !== "" && (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-start",
                              }}
                            >
                              <div style={{ marginTop: 12, marginRight: 15 }}>
                                <RightArrow />
                              </div>
                              <div>
                                <H2>{q.enunciado}</H2>
                              </div>
                            </div>
                          )}

                          <OMSQuestion
                            isView={false}
                            setTextAnswers={setTextAnswers}
                            setAllAnswers={setAllAnswers}
                            questionario={questionarioOMS}
                            perguntas={q.perguntas}
                            scalesLabel={q.labelEscalas}
                          />
                        </div>
                      </>
                    );
                  })}
                  <div style={{ marginTop: 32, marginBottom: 32 }}>
                    <GenericSolidButton
                      disabled={isFinalizeButtonBlocked}
                      color={Colors.SPM600}
                      onClick={answerQuestionnaire}
                    >
                      Finalizar questionário
                    </GenericSolidButton>
                  </div>

                  <div
                    style={{ borderTop: "2px solid #E8E8E8", marginBottom: 50 }}
                  >
                    <H2>Referências</H2>
                    <Text color={"#575757"}>{OMSQuestionnaireReference1}</Text>
                    <Text color={"#575757"}>{OMSQuestionnaireReference2}</Text>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      </GeneralContainerQuestionnaire>)}
    </>
  );
}

export default OMSQuestionnaire;
